import * as THREE from 'three/webgpu';
import type {EntityPatch} from '../../history/types';
import type {SketchArcObject} from '../../mesh/sketch/SketchArcObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {SketchDimensionRepository} from '../dimension/repository/SketchDimensionRepository';
import {
    createPlannedSketchDimensions,
    type PlannedSketchDimension,
} from '../dimension/createPlannedSketchDimensions';

/** 半径标注落在圆心到弧的这个比例处（与槽口自动尺寸同款摆位）。 */
const RADIUS_LABEL_RADIUS_RATIO = 0.55;

/** 倒角距离标注沿法向外移量相对距离值的比例。 */
const CHAMFER_LABEL_OFFSET_RATIO = 0.6;

/** 几何退化阈值（米）。 */
const DEGENERATE_LENGTH = 1e-9;

/** 读取点的草图局部二维坐标。 */
function readLocal(point: SketchPointObject): THREE.Vector2 {
    const local = point.getLocalPosition2D();

    return new THREE.Vector2(local.x, local.y);
}

/** 构造点引用记录。 */
function pointReference(point: SketchPointObject, selectionOrder: number) {
    return {
        entityId: point.persistentId,
        pointId: point.persistentId,
        topologyRole: 'point' as const,
        selectionOrder,
    };
}

/**
 * 圆角创建时自动添加半径尺寸（对齐 SW：草图圆角默认带半径尺寸约束）。
 *
 * 半径由 driving 尺寸直接编译的参数方程锁定；关系面板只显示尺寸派生快照，
 * 圆角服务不再另挂匿名半径关系——同一物理量只允许一个数值事实源。
 *
 * @param input 创建输入。
 * @returns 尺寸历史补丁；几何退化时为空数组。
 */
export function createFilletAutoDimensions(input: {
    readonly repository: SketchDimensionRepository;
    readonly sketchId: string;
    readonly arc: SketchArcObject;
}): EntityPatch[] {
    const definition = input.arc.getArcDefinition();
    const radius = definition.radius;

    if (!Number.isFinite(radius) || radius < DEGENERATE_LENGTH) {
        return [];
    }

    const centerLocal = readLocal(input.arc.getCenterPoint());
    const midWorld = input.arc.getMidpointPosition();
    const midLocalRaw = input.arc.referencePlane.worldToLocal(midWorld);
    const midLocal = new THREE.Vector2(midLocalRaw.x, midLocalRaw.y);
    const outward = midLocal.clone().sub(centerLocal);

    if (outward.lengthSq() < DEGENERATE_LENGTH * DEGENERATE_LENGTH) {
        return [];
    }

    const label = centerLocal.clone().add(outward.normalize().multiplyScalar(radius * RADIUS_LABEL_RADIUS_RATIO));
    const planned: PlannedSketchDimension[] = [{
        dimensionKind: 'radius',
        nominalValue: radius,
        references: [{
            entityId: input.arc.persistentId,
            topologyRole: 'entity',
            selectionOrder: 0,
        }],
        labelPosition: [label.x, label.y],
    }];

    return createPlannedSketchDimensions({
        repository: input.repository,
        sketchId: input.sketchId,
        planned,
    });
}

/**
 * 距离类倒角创建时自动添加两条距离尺寸（对齐 SW：倒角默认带两条距离尺寸约束）。
 *
 * 距离从**交点焦点**量到各倒角端点——正是 SW 图上沿两条源线标注的两个缩进量；
 * 距离锁由 driving 尺寸直接编译的参数方程提供；关系面板只显示尺寸派生快照，
 * 倒角服务不再另挂匿名距离关系。
 *
 * @param input 创建输入。
 * @returns 尺寸历史补丁；几何退化时为空数组。
 */
export function createChamferAutoDimensions(input: {
    readonly repository: SketchDimensionRepository;
    readonly sketchId: string;
    /** 线延长线交点焦点（虚拟尖角）。 */
    readonly focusPoint: SketchPointObject;
    /** 倒角线两端点。 */
    readonly firstPoint: SketchPointObject;
    readonly secondPoint: SketchPointObject;
}): EntityPatch[] {
    const focus = readLocal(input.focusPoint);
    const planned: PlannedSketchDimension[] = [];
    const endpoints: readonly SketchPointObject[] = [input.firstPoint, input.secondPoint];

    endpoints.forEach((endpoint, index) => {
        const other = endpoints[1 - index]!;
        const target = readLocal(endpoint);
        const along = target.clone().sub(focus);
        const distance = along.length();

        if (distance < DEGENERATE_LENGTH) {
            return;
        }

        /** 标注放在段中点、沿法向**背离另一端点**外移，避免压进倒角三角区。 */
        const direction = along.clone().normalize();
        const normal = new THREE.Vector2(-direction.y, direction.x);
        const towardOther = readLocal(other).sub(focus);

        if (normal.dot(towardOther) > 0) {
            normal.multiplyScalar(-1);
        }

        const label = focus.clone()
            .add(along.multiplyScalar(0.5))
            .add(normal.multiplyScalar(distance * CHAMFER_LABEL_OFFSET_RATIO));

        planned.push({
            dimensionKind: 'point-distance',
            nominalValue: distance,
            references: [pointReference(input.focusPoint, 0), pointReference(endpoint, 1)],
            labelPosition: [label.x, label.y],
        });
    });

    return createPlannedSketchDimensions({
        repository: input.repository,
        sketchId: input.sketchId,
        planned,
    });
}
